import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  useCallback, useEffect, useState, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import alarmsApi from '@api/alarmsApi';
import alarmsActions from '@state/alarms/actions';

function useAlarmas() {

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const alarmsList = useSelector(({ alarms: a }) => a.alarmsList);
  const offset = useSelector(({ alarms: a }) => a.offset);
  const limit = useSelector(({ alarms: a }) => a.limit);
  const alarmsListCount = useSelector(({ alarms: a }) => a.count);
  const search = useSelector(({ alarms: a }) => a.search);

  const isFirstTime = useRef(true);
  const timeout = useRef(null);
  const abortController = useRef(null);
  const unmountComponent = useRef(true);

  const [loading, setLoading] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);

  const [valueFilter, setValueFilter] = useState('');
  const [alarmsListArr, setAlarmsListArr] = useState([]);

  const [isCreate, setIsCreate] = useState(false);
  const currentUser = useSelector(({ user }) => user);

  const breadcrumbData = useMemo(() => {

    const currentSection = t('views.dynamic_alarms_create_edit.breadcrumb.dynamic_rules');

    return [
      [null, currentSection],
    ];

  }, [t]);

  const hasMore = useMemo(() => {

    if (!alarmsList) return true;
    return alarmsList.length < alarmsListCount;

  }, [alarmsListCount, alarmsList]);

  const onChangeSearch = useCallback(
    (event) => {

      const { value } = event.target;

      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(() => {

        dispatch(alarmsActions.resetOffset());
        dispatch(alarmsActions.setSearch(value));

      }, 1000);

    },
    // eslint-disable-next-line
    []
  );

  const goToCreate = useCallback(() => {

    history.push('/reglas/dinamicas/crear');
    // eslint-disable-next-line
  }, []);

  const requestAlarms = useCallback(
    ({ forNext = false } = {}) => {

      const abortC = new AbortController();
      const { signal } = abortC;
      abortController.current = abortC;

      const targetLoading = forNext ? setLoadingNext : setLoading;

      targetLoading(true);
      alarmsApi
        .getAlarmsRules({
          offset, limit, search, signal,
        })
        .then((response) => {

          if (unmountComponent) {

            const resp = {
              count: response.length,
              results: response,
            };
            const { count, results } = resp;
            dispatch(alarmsActions.setAlarmsList({ count, results }));

            setAlarmsListArr(results);
            targetLoading(false);

          }

        })
        .catch((resError) => {

          const { fem } = resError;
          // eslint-disable-next-line
          console.log(t(fem));
          targetLoading(false);

        });

    },
    // eslint-disable-next-line
    [offset, limit, search]
  );

  const abortRequest = useCallback(() => {

    const { current: abortC } = abortController;
    if (abortC) abortC.abort();

  }, []);

  const arrayFilter = alarmsListArr.filter((data) => ( // Filter by name
    data.name.toUpperCase().includes(valueFilter.toUpperCase())
  ));

  const getPermissonCreate = useCallback(() => {

    const currentUserRole = currentUser.role;
      // eslint-disable-next-line
      setIsCreate(!!(currentUserRole === 'Superadmin' || currentUserRole === 'Admin'));

  }, []);

  useEffect(() => {

    getPermissonCreate();

}, []);

  useEffect(() => {

    if (!alarmsList || isFirstTime.current) {

      isFirstTime.current = false;
      return;

    }

    requestAlarms();
    // eslint-disable-next-line consistent-return
    return abortRequest;
    // eslint-disable-next-line
  }, [search]);

  useEffect(() => {

    if (!alarmsList) {

      requestAlarms();

} else {

      setAlarmsListArr(alarmsList);

}
    return abortRequest;
    // eslint-disable-next-line
  }, []);

  useEffect(() => {

    if (alarmsList) {

      setAlarmsListArr(alarmsList);

}

  }, [alarmsList]);

  useEffect(() => () => {

    unmountComponent.current = false;

  });

  return {
    onChangeSearch,
    search,
    goToCreate,
    requestAlarms,
    alarmsList,
    loading,
    hasMore,
    loadingNext,
    breadcrumbData,
    setValueFilter,
    arrayFilter,
    isCreate,
  };

}

export default useAlarmas;
